import type { Appearance } from '@stripe/stripe-js';

/**
 * Внешний вид платёжной формы (FE-480, DESIGN_SYSTEM §7).
 *
 * Поля карты живут в iframe платёжной системы, и наш CSS до них не достаёт —
 * единственный способ привести их к дизайн-системе — передать значения через
 * `appearance`. Поэтому токены читаются из вычисленных стилей документа, а не
 * дублируются литералами: хардкод цветов и радиусов вне `tokens.css` запрещён
 * (CLAUDE.md §8), и вторая копия палитры разъехалась бы с первой при первой же
 * правке темы.
 *
 * Токены — канонические имена редизайна 2026-10 (DESIGN_SYSTEM §9): выбор,
 * фокус и активная вкладка — синий акцент `--color-accent`, а не красный CTA:
 * красная на экране оплаты только кнопка «оплатить» (§2.2). Рамка поля —
 * `--color-border-input`, как у наших полей ввода (§5.7): по ней поле карты
 * узнают как поле, и она держит 3:1 к фону (WCAG 1.4.11).
 *
 * Запасные значения нужны только для среды без стилей (unit-тест в jsdom): там
 * `getComputedStyle` возвращает пустые строки, а `Appearance` не принимает их.
 * Они повторяют `tokens.css` дословно, и расхождение ловит тест рядом.
 */
const FALLBACK = {
  '--color-surface': '#ffffff',
  '--color-text-primary': '#0e1b2e',
  '--color-text-secondary': '#3a4757',
  '--color-border': '#e1e8f1',
  '--color-border-input': '#7d8a99',
  '--color-danger': '#8e1b2b',
  '--color-accent': '#0a6fe0',
  '--radius-control': '12px',
  '--text-body': '1rem',
  '--font-sans':
    "'Instrument Sans Variable', 'Instrument Sans', 'Geist Variable', 'Geist', 'Segoe UI', system-ui, -apple-system, sans-serif",
} as const;

function token(name: keyof typeof FALLBACK): string {
  if (typeof window === 'undefined') return FALLBACK[name];
  // Перенос строки внутри объявления (стек шрифтов) схлопывается в пробел:
  // значение уходит в чужой iframe строкой, а не через каскад.
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .replace(/\s+/g, ' ')
    .trim();
  return value || FALLBACK[name];
}

export function stripeAppearance(): Appearance {
  return {
    theme: 'stripe',
    variables: {
      colorPrimary: token('--color-accent'),
      colorBackground: token('--color-surface'),
      colorText: token('--color-text-primary'),
      colorTextSecondary: token('--color-text-secondary'),
      colorDanger: token('--color-danger'),
      borderRadius: token('--radius-control'),
      spacingUnit: '4px',
      fontSizeBase: token('--text-body'),
      // Стек шрифтов — тот же токен, что у страницы. `inherit` здесь не
      // работает: iframe чужого домена ничего не наследует от нашей страницы, и
      // поля карты набирались шрифтом браузера по умолчанию — с засечками
      // (DESIGN_SYSTEM §3 их запрещает). Наши woff2 внутри iframe не
      // подгружаются, поэтому до Instrument Sans дело не доходит: стек
      // спускается к системному гротеску, а не к Times.
      fontFamily: token('--font-sans'),
    },
    rules: {
      '.Input': { borderColor: token('--color-border-input') },
      '.Tab': { borderColor: token('--color-border') },
    },
  };
}
